<script setup>
import { ref, computed, watch } from 'vue'
import { truncateText } from '../helpers.js'

const props = defineProps({
  completedSummary: { type: String, required: true },
  completedItems: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false }
})

const emit = defineEmits(['item-click', 'navigate-to-assessment'])

const isExpanded = ref(false)
const canExpand = computed(() => !props.loading && props.completedItems.length > 0)

watch(() => props.loading, (newVal, oldVal) => {
  if (oldVal === true && newVal === false && props.completedItems.length > 0) {
    isExpanded.value = true
  }
})

function toggleExpanded() {
  if (!canExpand.value) return
  isExpanded.value = !isExpanded.value
}

function handleItemClick(item) {
  emit('item-click', item)
}

function formatSource(source) {
  if (source === 'action_center') return 'Action Center'
  if (source === 'assessment_tool') return 'Assessment'
  return source || ''
}

function formatDate(isoString) {
  if (!isoString) return ''
  const date = new Date(isoString)
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const year = date.getFullYear()
  return `${month}/${day}/${year}`
}

function getTitle(item) {
  let title = item.question_action_center_title || item.question_title || ''

  if (typeof title === 'string') {
    try {
      const parsed = JSON.parse(title)
      if (Array.isArray(parsed)) title = parsed[0] || ''
      else title = parsed
    } catch (e) {
      // Keep as-is
    }
  }
  if (Array.isArray(title)) title = title[0] || ''

  return truncateText(title || `Question ${item.question_id}`)
}
</script>

<template>
  <div class="completed-section mt-3">
    <div
      class="d-flex justify-content-between align-items-center py-2 completed-header"
      :class="{ 'completed-header--disabled': !canExpand }"
      :role="canExpand ? 'button' : undefined"
      @click="toggleExpanded"
    >
      <p class="fw-bold text-16 mb-0" :class="canExpand ? 'text-primary' : 'text-muted'">
        <template v-if="loading">
          <span class="spinner-border spinner-border-sm me-2" role="status"></span>
        </template>
        {{ completedSummary }}
      </p>
      <i
        v-if="canExpand"
        class="bi text-primary"
        :class="isExpanded ? 'bi-caret-up-fill' : 'bi-caret-down-fill'"
      ></i>
    </div>

    <div v-if="isExpanded && canExpand">
      <p class="text-16 text-gray-700 mb-3">
        Completed items are based on answers on the
        <a href="#" class="text-primary" @click.prevent="emit('navigate-to-assessment')">Assessment</a>
        page or were marked complete here in the Action Center.
      </p>

      <ul class="list-unstyled mb-0">
        <li
          v-for="item in completedItems"
          :key="`completed-${item.question_id}-${item.id}`"
          class="d-flex justify-content-between align-items-start py-2 border-top"
        >
          <div class="flex-grow-1 ps-3">
            <button
              :id="`completedTitle-${item.question_id}`"
              class="btn btn-link p-0 text-decoration-none text-16 text-primary text-start w-100"
              type="button"
              @click="handleItemClick(item)"
            >
            <med>
              {{ getTitle(item) }}
            </med>
            </button>
            <div class="text-14 text-secondary mt-1">
              Completed in {{ formatSource(item.source) }} {{ formatDate(item.timestamp) }} by {{ item.user_fullname || 'Unknown' }}
            </div>
          </div>
          <div
            :id="`completedBtn-${item.question_id}`"
            class="text-16 fw-bold text-primary completed-label pe-4 flex-shrink-0"
            role="button"
            @click="handleItemClick(item)"
          >
            <strong>Completed</strong>
            <i class="bi bi-chevron-right ms-1"></i>
          </div>
        </li>
      </ul>
    </div>
  </div>
</template>

<style scoped lang="scss">
.completed-header {
  cursor: pointer;

  &--disabled {
    cursor: default;
    opacity: 0.7;
  }
}

.completed-label {
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    text-decoration: underline;
  }
}
</style>
